import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import {
  ArrowLeft, RefreshCw, AlertCircle, Search, ChevronRight, ShieldCheck,
  CreditCard, Wallet, Store, QrCode, Copy, Check, Clock, ExternalLink,
  CheckCircle2, X, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner'; // Delta #82: App hanya memasang Toaster sonner

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

const CATEGORY_META = {
  'QRIS':            { icon: QrCode,     accent: 'from-indigo-500 to-blue-500',    chip: 'bg-indigo-50 text-indigo-700' },
  'E-Wallet':        { icon: Wallet,     accent: 'from-purple-500 to-pink-500',    chip: 'bg-purple-50 text-purple-700' },
  'Virtual Account': { icon: CreditCard, accent: 'from-emerald-500 to-teal-500',   chip: 'bg-emerald-50 text-emerald-700' },
  'Retail':          { icon: Store,      accent: 'from-amber-500 to-orange-500',   chip: 'bg-amber-50 text-amber-700' },
};

const CAT_ORDER = ['QRIS', 'E-Wallet', 'Virtual Account', 'Retail'];

const TripayPayment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = searchParams.get('invoice_id');

  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState([]);
  const [baseAmount, setBaseAmount] = useState(0);
  const [search, setSearch] = useState('');
  const [creatingCode, setCreatingCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState(null); // TriPay create response (data)
  const [copied, setCopied] = useState(false);
  const [paidInfo, setPaidInfo] = useState(null); // { paid_date, payment_method, amount }
  const [checkingStatus, setCheckingStatus] = useState(false);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);

  const authHeader = () => {
    const t = localStorage.getItem('customer_token') || localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const goBackToPortal = () => {
    try {
      const slug = localStorage.getItem('tenant_slug');
      window.location.href = slug ? `/p/${slug}/dashboard` : '/customer/dashboard';
    } catch {
      window.location.href = '/customer/dashboard';
    }
  };

  const fetchChannels = async () => {
    if (!invoiceId) return;
    try {
      setLoading(true);
      setErrorMsg('');
      const r = await axios.get(`${API_URL}/api/v2/payments/tripay/channels?invoice_id=${invoiceId}`, { headers: authHeader() });
      if (r.data?.success) {
        setMethods(r.data.methods || []);
        setBaseAmount(r.data.amount || 0);
      } else {
        setErrorMsg('Gagal memuat metode pembayaran');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Gagal memuat metode';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChannels(); /* eslint-disable-next-line */ }, [invoiceId]);

  // Auto-polling status pembayaran setelah instruksi terbuat
  useEffect(() => {
    if (!result || !invoiceId || paidInfo) return;
    // Skip polling entirely if no auth token — anonymous payment link users.
    // Prevents 401 → global axios interceptor redirect ke /login.
    const _token = localStorage.getItem('customer_token') || localStorage.getItem('token');
    if (!_token) return;
    let _stopped = false;
    const checkStatus = async () => {
      if (_stopped) return;
      try {
        pollCountRef.current += 1;
        const r = await axios.get(`${API_URL}/api/v2/customer/invoice/${invoiceId}/status`, { headers: authHeader() });
        if (r.data?.success && r.data?.data?.is_paid) {
          setPaidInfo(r.data.data);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          toast.success('Pembayaran berhasil diterima!');
        } else if (pollCountRef.current > 180) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch (e) {
        // On 401/403 → stop polling permanently (don't spam or trigger redirect)
        const _sc = e?.response?.status;
        if (_sc === 401 || _sc === 403) {
          _stopped = true;
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      }
    };
    checkStatus();
    pollRef.current = setInterval(checkStatus, 5000);
    return () => {
      _stopped = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      pollCountRef.current = 0;
    };
    /* eslint-disable-next-line */
  }, [result, invoiceId, paidInfo]);

  const grouped = useMemo(() => {
    const q = search.trim().toUpperCase();
    const filtered = q ? methods.filter(m => (m.name + m.code).toUpperCase().includes(q)) : methods;
    const g = {};
    CAT_ORDER.forEach(c => { g[c] = []; });
    filtered.forEach(m => {
      const cat = CATEGORY_META[m.category] ? m.category : 'Virtual Account';
      if (!g[cat]) g[cat] = [];
      g[cat].push(m);
    });
    return g;
  }, [methods, search]);

  const handlePick = async (method) => {
    if (creatingCode) return;
    try {
      setCreatingCode(method.code);
      const r = await axios.post(
        `${API_URL}/api/v2/payments/tripay/create`,
        { invoice_id: parseInt(invoiceId), method: method.code },
        { headers: { 'Content-Type': 'application/json', ...authHeader() } }
      );
      if (r.data?.success && r.data?.data) {
        setResult({ ...r.data.data, methodCategory: method.category, methodName: method.name });
        toast.success(`Instruksi ${method.name} berhasil dibuat`);
      } else {
        toast.error('Gagal membuat pembayaran');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal membuat pembayaran');
    } finally {
      setCreatingCode(null);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      setCopied(true);
      toast.success('Disalin');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Gagal menyalin');
    }
  };

  const resetPicker = () => {
    setResult(null);
    setCopied(false);
    setPaidInfo(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    pollCountRef.current = 0;
  };

  // Delta #82: tombol cek status manual ke TriPay
  const checkTripayStatus = async () => {
    setCheckingStatus(true);
    try {
      const r = await axios.post(`${API_URL}/api/v2/payments/tripay/check-status`, { invoice_id: Number(invoiceId), reference: result?.reference || '' });
      const st = r.data?.status;
      if (r.data?.invoice_paid) {
        toast.success('Pembayaran berhasil diterima!');
        setPaidInfo({ ...(r.data.paid_info || {}), payment_method: result?.methodName });
      } else if (st === 'PAID') {
        toast('Pembayaran terdeteksi di TriPay dan sedang diproses. Hubungi admin bila status belum berubah.', { duration: 8000 });
      } else if (st === 'EXPIRED' || st === 'FAILED') {
        toast.error('Transaksi ini sudah kedaluwarsa di TriPay. Jika saldo sudah terpotong, simpan bukti bayar lalu hubungi admin.', { duration: 8000 });
      } else {
        toast('Pembayaran belum diterima TriPay. Jika saldo sudah terpotong, tunggu beberapa menit lalu cek lagi.', { duration: 7000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal cek status pembayaran');
    } finally {
      setCheckingStatus(false);
    }
  };

  // ==================== RENDER: PAID SUCCESS ====================
  if (paidInfo) {
    return (
      <div className="min-h-screen py-6 sm:py-10 px-4 flex items-center bg-slate-50" data-testid="tripay-paid-view">
        <div className="max-w-md mx-auto w-full">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-emerald-100">
            <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white p-8 text-center overflow-hidden">
              <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full backdrop-blur-sm mb-4 animate-pulse">
                  <CheckCircle2 className="w-12 h-12" />
                </div>
                <h1 className="text-2xl font-bold">Pembayaran Berhasil!</h1>
                <p className="text-white/90 text-sm mt-2">Terima kasih, tagihan Anda sudah lunas.</p>
              </div>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-500 text-sm">Metode</span>
                <span className="font-semibold text-slate-800">TriPay - {result?.methodName || paidInfo.payment_method || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-500 text-sm">Nominal</span>
                <span className="font-semibold text-slate-800">{fmtIDR(paidInfo.amount || baseAmount)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-slate-500 text-sm">Tanggal</span>
                <span className="font-semibold text-slate-800">{paidInfo.paid_date || '-'}</span>
              </div>
            </div>
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-100">
              <Button onClick={goBackToPortal} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="tripay-paid-back-btn">
                Kembali ke Portal
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== RENDER: RESULT (VA/QR/Instructions) ====================
  if (result) {
    const isQris = (result.methodCategory === 'QRIS') || !!result.qr_url || !!result.qr_string;
    const isRetail = result.methodCategory === 'Retail';
    const isEwallet = result.methodCategory === 'E-Wallet';
    const payCode = result.pay_code || result.pay_url || '';
    const expiryDate = result.expired_time ? new Date(result.expired_time * 1000) : null;
    const expiryStr = expiryDate ? expiryDate.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

    return (
      <div className="min-h-screen py-6 px-4 bg-slate-50" data-testid="tripay-result-view">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button onClick={resetPicker} className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="tripay-change-method-btn">
              <ArrowLeft className="w-4 h-4" /> Ganti Metode
            </button>
            <button onClick={goBackToPortal} className="flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm" data-testid="tripay-result-close-btn">
              <X className="w-4 h-4" /> Tutup
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200" data-testid="tripay-result-card">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6 text-white">
              <p className="text-xs uppercase tracking-wide opacity-80">{result.methodName || result.payment_name || 'Pembayaran'}</p>
              <p className="text-3xl font-bold mt-1" data-testid="tripay-result-amount">{fmtIDR(result.amount || baseAmount)}</p>
              <div className="flex items-center gap-2 mt-2 text-sm opacity-90">
                <Clock className="w-4 h-4" />
                <span data-testid="tripay-result-expiry">Bayar sebelum: {expiryStr}</span>
              </div>
            </div>

            {/* Customer + Invoice info */}
            <div className="px-6 pt-4 pb-2 border-b border-slate-100 grid grid-cols-2 gap-3 text-sm" data-testid="tripay-customer-info">
              <div>
                <p className="text-xs text-slate-500">Nama Pelanggan</p>
                <p className="font-semibold text-slate-800 truncate" data-testid="tripay-customer-name">{result.customer_name || '-'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">No. Invoice</p>
                <p className="font-mono font-semibold text-slate-800 truncate" data-testid="tripay-invoice-number">
                  {(Array.isArray(result.order_items) && result.order_items[0]?.sku) || '-'}
                </p>
              </div>
            </div>

            {/* Payment code / VA / QR display */}
            <div className="p-6 space-y-4">
              {isQris && (result.qr_string || result.qr_url) && (
                <div className="flex flex-col items-center gap-3 bg-slate-50 rounded-xl p-6" data-testid="tripay-qris-block">
                  {result.qr_url ? (
                    <img src={result.qr_url} alt="QRIS" className="w-56 h-56" />
                  ) : (
                    <QRCodeSVG value={result.qr_string || ''} size={224} />
                  )}
                  <p className="text-sm text-slate-600 text-center">Scan kode QR di atas dengan aplikasi e-wallet atau mobile banking Anda</p>
                </div>
              )}

              {!isQris && payCode && (
                <div className="bg-slate-50 rounded-xl p-4" data-testid="tripay-paycode-block">
                  <p className="text-xs text-slate-500 mb-1">{isRetail ? 'Kode Bayar' : 'Nomor Virtual Account'}</p>
                  <div className="flex items-center gap-3">
                    <p className="text-xl sm:text-2xl font-mono font-bold text-slate-800 flex-1 min-w-0 break-all tracking-wider" data-testid="tripay-paycode">{payCode}</p>
                    <button
                      onClick={() => copyText(payCode)}
                      className="flex-shrink-0 flex items-center gap-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition"
                      data-testid="tripay-copy-btn"
                    >
                      {copied ? <><Check className="w-4 h-4" /> Tersalin</> : <><Copy className="w-4 h-4" /> Salin</>}
                    </button>
                  </div>
                </div>
              )}

              {isEwallet && (result.checkout_url || result.pay_url) && (
                <a
                  href={result.pay_url || result.checkout_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition"
                  data-testid="tripay-ewallet-open-btn"
                >
                  Buka Aplikasi {result.methodName || 'E-Wallet'} <ExternalLink className="w-4 h-4" />
                </a>
              )}

              {/* Amount breakdown */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Nominal Invoice</span>
                  <span className="font-medium text-slate-700">{fmtIDR(result.amount_received || baseAmount)}</span>
                </div>
                {result.total_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Biaya Admin</span>
                    <span className="font-medium text-slate-700">{fmtIDR(result.total_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-slate-200">
                  <span className="font-semibold text-slate-700">Total Bayar</span>
                  <span className="font-bold text-indigo-600 text-base">{fmtIDR(result.amount)}</span>
                </div>
              </div>

              {/* Instructions collapsible */}
              {Array.isArray(result.instructions) && result.instructions.length > 0 && (
                <details className="bg-slate-50 rounded-xl" data-testid="tripay-instructions">
                  <summary className="p-4 font-semibold text-slate-700 cursor-pointer select-none">
                    Cara Pembayaran ({result.instructions.length} opsi)
                  </summary>
                  <div className="px-4 pb-4 space-y-4">
                    {result.instructions.map((ins, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="font-semibold text-slate-800 text-sm mb-2">{ins.title}</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs text-slate-600">
                          {(ins.steps || []).map((s, i) => (
                            <li key={i} dangerouslySetInnerHTML={{ __html: s }} />
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Status polling indicator */}
              <div className="flex items-center justify-center gap-2 text-xs text-slate-500 py-2">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Menunggu pembayaran... halaman akan otomatis update ketika lunas
              </div>
              <Button
                variant="outline"
                onClick={checkTripayStatus}
                disabled={checkingStatus}
                className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                data-testid="tripay-check-status-btn"
              >
                {checkingStatus ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Saya sudah bayar, cek status
              </Button>
            </div>

            {/* Footer actions */}
            <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={resetPicker}
                className="flex-1"
                data-testid="tripay-result-ganti-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Ganti Metode
              </Button>
              <Button
                onClick={goBackToPortal}
                className="flex-1 bg-slate-800 hover:bg-slate-900 text-white"
                data-testid="tripay-result-selesai-btn"
              >
                Selesai & Kembali
              </Button>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-4">
            Powered by <span className="font-semibold">TriPay</span> · Transaksi aman & terenkripsi
          </p>
        </div>
      </div>
    );
  }

  // ==================== RENDER: METHOD PICKER (default) ====================
  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={goBackToPortal}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          data-testid="tripay-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali
        </button>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200" data-testid="tripay-payment-card">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide opacity-80">Pilih Metode Pembayaran</p>
                <p className="text-3xl font-bold mt-1" data-testid="tripay-amount">{fmtIDR(baseAmount)}</p>
                <p className="text-xs opacity-80 mt-1">Nominal invoice yang harus dibayar</p>
              </div>
              <button
                onClick={fetchChannels}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition"
                title="Refresh"
                data-testid="tripay-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-slate-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari metode (BRIVA, QRIS, OVO, dll.)"
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="tripay-search-input"
              />
            </div>
          </div>

          {/* Body */}
          <div className="p-6 min-h-[300px]">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <RefreshCw className="w-8 h-8 animate-spin mb-3" />
                <p>Memuat metode pembayaran...</p>
              </div>
            ) : errorMsg ? (
              <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="tripay-error-panel">
                <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
                <p className="font-semibold text-slate-700">Gagal memuat metode</p>
                <p className="text-sm text-slate-500 mt-1">{errorMsg}</p>
                <Button onClick={fetchChannels} variant="outline" className="mt-4" data-testid="tripay-retry-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Coba lagi
                </Button>
              </div>
            ) : methods.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="w-12 h-12 text-amber-400 mb-3" />
                <p className="font-semibold text-slate-700">Belum ada metode aktif</p>
                <p className="text-sm text-slate-500 mt-1">Silakan aktifkan channel TriPay di Payment Gateway Settings.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {CAT_ORDER.map(cat => {
                  const list = grouped[cat] || [];
                  if (!list.length) return null;
                  const Meta = CATEGORY_META[cat];
                  const Icon = Meta.icon;
                  return (
                    <div key={cat} data-testid={`tripay-cat-${cat.replace(/\s+/g,'-').toLowerCase()}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${Meta.accent} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="font-semibold text-slate-800">{cat}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${Meta.chip}`}>{list.length}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {list.map(m => (
                          <button
                            key={m.code}
                            onClick={() => handlePick(m)}
                            disabled={creatingCode !== null}
                            className={`flex items-center justify-between p-3 border rounded-xl text-left transition ${creatingCode === m.code ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'} ${creatingCode ? 'opacity-70 cursor-not-allowed' : ''}`}
                            data-testid={`tripay-method-${m.code.toLowerCase()}`}
                          >
                            <div>
                              <p className="font-medium text-sm text-slate-800">{m.name}</p>
                              <p className="text-xs text-slate-500">{m.code}</p>
                            </div>
                            {creatingCode === m.code ? (
                              <RefreshCw className="w-4 h-4 animate-spin text-indigo-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Transaksi aman & terenkripsi
            </div>
            <div>Powered by <span className="font-semibold">TriPay</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripayPayment;
